import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { gamificationService, GamificationDelta, GamificationLevelsStatusResponse } from '../services/gamification/gamification.service';

export interface GamificationState {
  totalPoints: number;
  levelKey: string;
  levelLabel: string;
  levelIcon: string;
  classMedian: number;
  currentStreak: number;
  longestStreak: number;
  targetStreakDays: number | null;
  lastActivityDate: string | null;
  isLoading: boolean;
  pointsConfig: Record<string, number>;
  lastUpdatedAt: number;
  levelsStatus: GamificationLevelsStatusResponse | null;
  isLevelsLoading: boolean;

  initState: () => Promise<void>;
  fetchLevelsStatus: () => Promise<GamificationLevelsStatusResponse | null>;
  setAward: (
    actionKey: string,
    context?: Record<string, unknown>
  ) => Promise<GamificationDelta | null>;
  setAwardAndGetSummary: (
    actionKey: string,
    context?: Record<string, unknown>
  ) => Promise<{ points: number; percentile: number; cohort_size: number; gamification_state: GamificationDelta } | null>;
  setGamificationState: (patch: Partial<GamificationState>, markFresh?: boolean) => void;
  resetState: () => void;
}

const getClientTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "Asia/Jerusalem";
  }
};

let initPromise: Promise<void> | null = null;
let inFlightAwards = 0;
let latestAwardSeq = 0;

export const useGamificationStore = create(
  persist<GamificationState>((set, get) => ({
  totalPoints: 0,
  levelKey: 'beginner',
  levelLabel: '',
  levelIcon: '',
  classMedian: 0,
  currentStreak: 0,
  longestStreak: 0,
  targetStreakDays: null,
  lastActivityDate: null,
  isLoading: false,
  pointsConfig: {},
  lastUpdatedAt: 0,
  levelsStatus: null,
  isLevelsLoading: false,

  fetchLevelsStatus: async () => {
    set({ isLevelsLoading: true });
    try {
      const data = await gamificationService.getLevelsStatus();
      if (data) {
        set({ levelsStatus: data, isLevelsLoading: false });
        return data;
      }
    } catch (error) {
      console.error("Failed to fetch levels status", error);
    }
    set({ isLevelsLoading: false });
    return null;
  },

  initState: async () => {
    // Skip if updated very recently (e.g. during login) to avoid race conditions
    // Skip if updated very recently UNLESS we are missing the levelIcon
    if (get().lastUpdatedAt > Date.now() - 5000 && get().levelIcon) return;

    // Deduplication: If already fetching, return the existing promise
    if (initPromise) return initPromise;

    initPromise = (async () => {
      set({ isLoading: true });
      try {
        const [state, configArr] = await Promise.all([
          gamificationService.getState(),
          gamificationService.getConfig()
        ]);
        const configMap: Record<string, number> = {};
        if (Array.isArray(configArr)) {
          configArr.forEach((c: any) => configMap[c.action_key] = c.points);
        }
        if (state) {
          set((prev) => ({
            totalPoints: inFlightAwards > 0 ? prev.totalPoints : state.total_points,
            levelKey: state.level_key,
            levelLabel: state.level_label,
            levelIcon: state.level_icon || '',
            classMedian: state.class_median || 0,
            currentStreak: state.current_streak,
            longestStreak: state.longest_streak,
            targetStreakDays: state.target_streak_days ?? null,
            lastActivityDate: state.last_activity_date,
            pointsConfig: Object.keys(configMap).length > 0 ? configMap : prev.pointsConfig,
            lastUpdatedAt: Date.now(),
          }));
        }
      } catch (error) {
        console.error("Failed to initialize gamification state", error);
      } finally {
        set({ isLoading: false });
        initPromise = null; // Reset lock
      }
    })();

    return initPromise;
  },

  setAward: async (actionKey, context = {}) => {
    const timezone = getClientTimezone();
    const optimisticDelta = get().pointsConfig[actionKey] ?? 0;
    inFlightAwards++;
    const seq = ++latestAwardSeq;

    if (optimisticDelta !== 0) {
      set((state) => ({
        totalPoints: Math.max(0, state.totalPoints + optimisticDelta),
        lastUpdatedAt: Date.now(),
      }));
    }

    try {
      const result = await gamificationService.setAward(actionKey, context, timezone);
      if (result) {
        const actualDelta = result.delta ?? 0;
        const deltaCorrection = actualDelta - optimisticDelta;
        set((state) => ({
          totalPoints: (inFlightAwards === 1 && seq === latestAwardSeq)
            ? result.total_points
            : Math.max(0, state.totalPoints + deltaCorrection),
          levelKey: result.level_key,
          levelLabel: result.level_label,
          levelIcon: result.level_icon || '',
          classMedian: result.class_median !== undefined ? result.class_median : state.classMedian,
          currentStreak: result.current_streak,
          longestStreak: result.longest_streak,
          targetStreakDays: result.target_streak_days !== undefined ? result.target_streak_days : state.targetStreakDays,
          lastActivityDate: result.last_activity_date,
          lastUpdatedAt: Date.now(),
        }));
        return result;
      } else if (optimisticDelta !== 0) {
        set((state) => ({
          totalPoints: Math.max(0, state.totalPoints - optimisticDelta),
        }));
      }
    } catch (error) {
      if (optimisticDelta !== 0) {
        set((state) => ({
          totalPoints: Math.max(0, state.totalPoints - optimisticDelta),
        }));
      }
      console.error("Failed to set award", error);
    } finally {
      inFlightAwards = Math.max(0, inFlightAwards - 1);
    }
    return null;
  },

  setAwardAndGetSummary: async (actionKey, context = {}) => {
    const timezone = getClientTimezone();
    const optimisticDelta = get().pointsConfig[actionKey] ?? 0;
    inFlightAwards++;
    const seq = ++latestAwardSeq;

    if (optimisticDelta !== 0) {
      set((state) => ({
        totalPoints: Math.max(0, state.totalPoints + optimisticDelta),
        lastUpdatedAt: Date.now(),
      }));
    }

    try {
      const result = await gamificationService.setAwardAndGetSummary(actionKey, context, timezone);
      if (result && result.gamification_state) {
        const actualDelta = result.gamification_state.delta ?? 0;
        const deltaCorrection = actualDelta - optimisticDelta;
        set((state) => ({
          totalPoints: (inFlightAwards === 1 && seq === latestAwardSeq)
            ? result.gamification_state.total_points
            : Math.max(0, state.totalPoints + deltaCorrection),
          levelKey: result.gamification_state.level_key,
          levelLabel: result.gamification_state.level_label,
          levelIcon: result.gamification_state.level_icon || '',
          currentStreak: result.gamification_state.current_streak,
          longestStreak: result.gamification_state.longest_streak,
          targetStreakDays: result.gamification_state.target_streak_days !== undefined ? result.gamification_state.target_streak_days : state.targetStreakDays,
          lastActivityDate: result.gamification_state.last_activity_date,
          lastUpdatedAt: Date.now(),
        }));
        return result;
      } else if (optimisticDelta !== 0) {
        set((state) => ({
          totalPoints: Math.max(0, state.totalPoints - optimisticDelta),
        }));
      }
    } catch (error) {
      if (optimisticDelta !== 0) {
        set((state) => ({
          totalPoints: Math.max(0, state.totalPoints - optimisticDelta),
        }));
      }
      console.error("Failed to set award and get summary", error);
    } finally {
      inFlightAwards = Math.max(0, inFlightAwards - 1);
    }
    return null;
  },

  setGamificationState: (patch, markFresh = false) => {
    set((state) => ({ 
      ...state, 
      ...patch,
      lastUpdatedAt: markFresh ? Date.now() : state.lastUpdatedAt 
    }));
  },

  resetState: () => {
    set({
      totalPoints: 0,
      levelKey: 'beginner',
      levelLabel: '',
      levelIcon: '',
      classMedian: 0,
      currentStreak: 0,
      longestStreak: 0,
      targetStreakDays: null,
      lastActivityDate: null,
      isLoading: false,
      pointsConfig: {},
      lastUpdatedAt: 0,
      levelsStatus: null,
      isLevelsLoading: false,
    });
  }
  }),
  {
    name: "shoonit-gamification",
    storage: createJSONStorage(() => localStorage),
  })
);

// Export the store hook and a direct accessor
export const gamificationStore = useGamificationStore;
